import { useState, useEffect, type FormEvent } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import { supabase } from '@/lib/supabase';

interface BankAccount {
  id: string;
  bank_name: string;
  account_number: string;
  account_holder: string;
  is_default: boolean;
}

const bankList = [
  'Vietcombank',
  'VietinBank',
  'BIDV',
  'Agribank',
  'Techcombank',
  'ACB',
  'Sacombank',
  'VPBank',
  'MB',
  'SHB',
  'VIB',
  'TPBank',
  'Eximbank',
  'HDBank',
  'OCB',
  'SeABank',
  'MSB',
  'Nam A Bank',
  'NCB',
  'OceanBank',
  'PGBank',
  'ABBank',
  'Bac A Bank',
  'Bao Viet Bank',
  'BVBank',
  'CB Bank',
  'Dong A Bank',
  'GPBank',
  'Kienlongbank',
  'LienVietPostBank',
  'PVcomBank',
  'Saigonbank',
  'SCB',
  'Viet A Bank',
  'VietBank',
  'Viet Capital Bank',
  'CIMB Vietnam',
  'Hong Leong Bank',
  'HSBC Vietnam',
  'Public Bank Vietnam',
  'Shinhan Bank',
  'Standard Chartered',
  'UOB Vietnam',
  'Woori Bank',
  'Indovina Bank',
  'VRB (Việt Nga)',
  'Kasikornbank',
];

export default function Bank() {
  const [showForm, setShowForm] = useState(false);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadBanks = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setLoadError('Vui lòng đăng nhập để xem ngân hàng liên kết.');
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.rpc('get_user_banks_json', {
        p_user_id: userData.user.id,
      });
      if (error) throw error;
      const result = Array.isArray(data) ? data : JSON.parse((data as string) || '[]');
      setBanks((result || []) as BankAccount[]);
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Không thể tải danh sách ngân hàng.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanks();
  }, []);

  const resetForm = () => {
    setBankName('');
    setAccountNumber('');
    setAccountHolder('');
    setFormError('');
    setFormSuccess('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!bankName) {
      setFormError('Vui lòng chọn ngân hàng.');
      return;
    }
    if (!/^\d{6,20}$/.test(accountNumber.trim())) {
      setFormError('Số tài khoản không hợp lệ. Vui lòng nhập 6 - 20 chữ số.');
      return;
    }
    if (accountHolder.trim().length < 3) {
      setFormError('Vui lòng nhập đầy đủ tên chủ tài khoản (không dấu).');
      return;
    }

    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setFormError('Vui lòng đăng nhập để liên kết ngân hàng.');
        return;
      }

      const { error } = await supabase.from('bank_accounts').insert({
        user_id: userData.user.id,
        bank_name: bankName,
        account_number: accountNumber.trim(),
        account_holder: accountHolder.trim().toUpperCase(),
      });

      if (error) throw error;

      setFormSuccess('Liên kết ngân hàng thành công!');
      resetForm();
      setShowForm(false);
      await loadBanks();
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Không thể liên kết ngân hàng. Vui lòng thử lại.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from('bank_accounts').delete().eq('id', id);
      if (error) throw error;
      setBanks((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Không thể gỡ tài khoản ngân hàng.';
      setLoadError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950">Ngân hàng liên kết</h1>
            <p className="text-foreground-600 mt-1">Quản lý tài khoản ngân hàng để nạp và rút tiền.</p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setFormError('');
              setFormSuccess('');
            }}
            className="px-4 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-add-line mr-1"></i> Liên kết mới
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="rounded-xl border border-background-200/60 bg-background-100 p-6 mb-6">
            <h3 className="text-base font-semibold text-foreground-950 mb-4">Thêm tài khoản ngân hàng</h3>

            {formError && (
              <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-rose-400 text-base"></i>
                <p className="text-sm text-rose-400">{formError}</p>
              </div>
            )}
            {formSuccess && (
              <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
                <i className="ri-check-line text-emerald-400 text-base"></i>
                <p className="text-sm text-emerald-400">{formSuccess}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tên ngân hàng</label>
                <select
                  name="bank_name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500"
                >
                  <option value="" disabled>Chọn ngân hàng</option>
                  {bankList.map((bank) => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Số tài khoản</label>
                <input
                  name="account_number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Nhập số tài khoản"
                  inputMode="numeric"
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Chủ tài khoản</label>
                <input
                  name="account_holder"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder="Tên chủ tài khoản (không dấu)"
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
              >
                {submitting ? 'Đang xử lý...' : 'Xác nhận liên kết'}
              </button>
            </div>
          </form>
        )}

        {loadError && (
          <div className="mb-6 rounded-lg bg-rose-500/15 border border-rose-500/30 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-rose-400 text-base"></i>
              <p className="text-sm text-rose-400">{loadError}</p>
            </div>
            <button onClick={loadBanks} className="text-sm font-semibold text-rose-400 hover:text-rose-300 whitespace-nowrap cursor-pointer">
              Thử lại
            </button>
          </div>
        )}

        <div className="space-y-4">
          {loading ? (
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-8 text-center">
              <i className="ri-loader-4-line animate-spin text-foreground-400 text-2xl"></i>
              <p className="text-sm text-foreground-600 mt-2">Đang tải...</p>
            </div>
          ) : banks.length === 0 ? (
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-lg bg-background-200/60 flex items-center justify-center mb-3">
                <i className="ri-bank-line text-foreground-500 text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-600">Bạn chưa liên kết tài khoản ngân hàng nào</p>
              <p className="text-xs text-foreground-500 mt-1">Bấm "Liên kết mới" để thêm tài khoản nhận/rút tiền.</p>
            </div>
          ) : (
            banks.map((b) => (
              <div key={b.id} className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-primary-500/15 flex items-center justify-center">
                      <i className="ri-bank-line text-primary-400 text-xl"></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold text-foreground-950">{b.bank_name}</p>
                        {b.is_default && <span className="px-2 py-0.5 rounded-full bg-primary-500/15 text-primary-400 text-xs font-semibold">Mặc định</span>}
                      </div>
                      <p className="text-sm text-foreground-600">{b.account_number} · {b.account_holder}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(b.id)}
                    disabled={deletingId === b.id}
                    className="text-sm font-semibold text-foreground-600 hover:text-rose-400 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {deletingId === b.id ? (
                      <i className="ri-loader-4-line animate-spin mr-1"></i>
                    ) : (
                      <i className="ri-delete-bin-6-line mr-1"></i>
                    )}
                    Gỡ
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AccountLayout>
  );
}